import { useState } from 'react'

import { Box, Card, Text } from '@factorialco/deprecated-design-system'
import { Moment } from 'moment'

import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { SegmentedControl } from 'modules/project_management/components/SegmentedControl'
import { BreakdownEvolutionChart } from 'modules/project_management/components/charts/EvolutionChart/BreakdownEvolutionChart'
import { CostEvolutionChart } from 'modules/project_management/components/charts/EvolutionChart/CostEvolutionChart'
import { CostOptions } from 'modules/project_management/components/charts/EvolutionChart/CostOptions'
import { TimeEvolutionChart } from 'modules/project_management/components/charts/EvolutionChart/TimeEvolutionChart/indext'
import {
  graphViewsCostOptionEvolutionEnum,
  overviewFrequencyOptionsEnum,
} from 'modules/project_management/lib/constants'
import { OverviewFilterOption } from 'modules/project_management/lib/overviewOptions'

interface EvolutionChartProps {
  projectId: number
  startDate?: Moment
  endDate?: Moment
  selectedFrequency: overviewFrequencyOptionsEnum
  selectedFilter: OverviewFilterOption
  plannedMinutes?: number
  plannedCents?: number
  plannedIncome?: number
  canSeeCosts: boolean
  canSeeLaborCosts: boolean
  currency: string
  hasBudgetFeature: boolean
}

export const EvolutionChart: React.FC<EvolutionChartProps> = ({
  projectId,
  startDate,
  endDate,
  selectedFilter,
  selectedFrequency,
  plannedMinutes,
  plannedCents,
  plannedIncome,
  canSeeCosts,
  canSeeLaborCosts,
  currency,
  hasBudgetFeature,
}) => {
  const [currentChart, setCurrentChart] = useState<number>(0)
  const [selectedOption, setSelectedOption] = useState(graphViewsCostOptionEvolutionEnum.evolution)

  const segments = [
    { label: i18n.t('project_management.graphs.segments.hours.title') },
    { label: i18n.t('project_management.graphs.segments.cost.title') },
  ]

  const changeChart = (index: number) => {
    setCurrentChart(index)
    setSelectedOption(graphViewsCostOptionEvolutionEnum.evolution)
  }

  useEffect(() => {
    track(trackingEvents.projectManagement.overview.seeEvolutionChart, {
      data_information: currentChart === 0 ? 'hours' : 'cost',
    })
  }, [currentChart])

  const timeTitle = (
    <Text size="400" weight="semibold" color="grey1000">
      {i18n.t('project_management.graphs.evolution.time.title')}
    </Text>
  )

  const costTitle = (
    <Text size="400" weight="semibold" color="grey1000">
      {i18n.t('project_management.graphs.evolution.cost.title')}
    </Text>
  )

  const handleOptionSelect = (newSelectedOption: graphViewsCostOptionEvolutionEnum) => {
    setSelectedOption(newSelectedOption)
  }
  const costOptions = <CostOptions onOptionSelect={handleOptionSelect} />

  const titleOption = currentChart === 0 ? timeTitle : canSeeLaborCosts ? costOptions : costTitle

  return (
    <Card>
      <Card.Content>
        <>
          <Box flexDirection="row" justifyContent="spaceBetween" width="full">
            {titleOption}
            {canSeeCosts && (
              <SegmentedControl
                segments={segments}
                onChangeSelected={changeChart}
                layoutId="evolutionChart"
              />
            )}
          </Box>
          <Box width="full">
            {currentChart === 0 ? (
              <TimeEvolutionChart
                projectId={projectId}
                startDate={startDate}
                endDate={endDate}
                selectedFilter={selectedFilter}
                selectedFrequency={selectedFrequency}
                plannedMinutes={plannedMinutes}
                hasBudgetFeature={hasBudgetFeature}
              />
            ) : selectedOption === 'evolution' ? (
              <CostEvolutionChart
                projectId={projectId}
                startDate={startDate}
                endDate={endDate}
                plannedCents={plannedCents}
                feeAmountCents={plannedIncome}
                selectedFrequency={selectedFrequency}
                selectedFilter={selectedFilter}
                currency={currency}
              />
            ) : (
              <BreakdownEvolutionChart
                projectId={projectId}
                startDate={startDate}
                endDate={endDate}
                selectedFilter={selectedFilter}
                selectedFrequency={selectedFrequency}
                currency={currency}
              />
            )}
          </Box>
        </>
      </Card.Content>
    </Card>
  )
}
